<template>
  <main v-if="enabled && isWechat && !isVerified" class="wechat-slider-gate" aria-label="访问验证">
    <section class="slider-card" aria-labelledby="slider-gate-title">
      <h1 id="slider-gate-title">请完成验证</h1>
      <p>请将滑块拖动至最右侧后继续访问</p>

      <div class="slider-track" :class="{ 'is-dragging': isDragging }">
        <div class="slider-progress" :style="{ width: `${sliderValue}%` }"/>
        <span class="slider-text">{{ sliderText }}</span>
        <input
            v-model.number="sliderValue"
            class="slider-input"
            type="range"
            min="0"
            max="100"
            step="1"
            aria-label="向右拖动完成验证"
            @pointerdown="startDrag"
            @pointerup="finishDrag"
            @pointercancel="finishDrag"
            @touchend="finishDrag"
            @input="isDragging = true"
            @change="finishDrag"
        />
        <span class="slider-handle" :style="{ left: `calc(${sliderValue}% - ${sliderValue * 0.44}px)` }"
              aria-hidden="true">
          <span>›</span>
        </span>
      </div>

      <button class="copy-link-button" type="button" @click="copyCurrentLink">
<!--        <span class="copy-link-icon" aria-hidden="true">↗</span>-->
        复制链接
      </button>


    </section>
  </main>
</template>

<script setup lang="ts">
import {computed, onMounted, ref} from 'vue';
import {showMsg} from '@/utils/msg';

interface Props {
  enabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  enabled: true,
});

const emit = defineEmits<{
  (event: 'verified'): void;
}>();

const isWechat = ref(false);
const sliderValue = ref(0);
const isDragging = ref(false);
const isVerified = ref(false);

const sliderText = computed(() =>
    isVerified.value ? '验证成功' : '向右拖动滑块验证',
);

function finishDrag(): void {
  isDragging.value = false;

  if (sliderValue.value < 96 || isVerified.value) {
    sliderValue.value = 0;
    return;
  }

  sliderValue.value = 100;
  isVerified.value = true;
  emit('verified');
}

function startDrag(): void {
  isDragging.value = true;
}

async function copyCurrentLink(): Promise<void> {
  const link = window.location.href;
  let copied = false;

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(link);
      copied = true;
    }
  } catch {
    copied = false;
  }

  if (!copied) {
    const textarea = document.createElement('textarea');
    textarea.value = link;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';
    document.body.appendChild(textarea);
    textarea.select();
    copied = document.execCommand('copy');
    document.body.removeChild(textarea);
  }

  showMsg(copied ? '链接复制成功，可用浏览器进行访问。' : '复制失败，请手动复制链接');
}

onMounted(() => {
  if (!props.enabled) {
    emit('verified');
    return;
  }

  // isWechat.value = true;

  try {
    isWechat.value = /MicroMessenger/i.test(navigator.userAgent || '');
  } catch {
    isWechat.value = false;
  }

  // 非微信环境不显示开关，直接挂载原有页面。
  if (!isWechat.value) {
    emit('verified');
  }
});
</script>

<style scoped>
.wechat-slider-gate {
  position: fixed;
  z-index: 9999;
  inset: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  box-sizing: border-box;
  min-height: 100vh;
  padding: clamp(88px, 32vh, 300px) 20px 24px;
  background: #fff;
}

.slider-card {
  box-sizing: border-box;
  width: min(100%, 400px);
  color: #1f2937;
}

.slider-card h1 {
  margin: 0;
  font-size: 19px;
  font-weight: 600;
  line-height: 1.5;
  text-align: center;
}

.slider-card p {
  margin: 8px 0 26px;
  color: #8a8f99;
  font-size: 14px;
  line-height: 1.6;
  text-align: center;
}

.slider-track {
  position: relative;
  height: 50px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f6f7f9;
  user-select: none;
  touch-action: none;
}

.slider-progress {
  position: absolute;
  inset: 0 auto 0 0;
  background: #e6f0ff;
  transition: width 0.12s ease-out;
}

.slider-text {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9aa0aa;
  font-size: 14px;
  pointer-events: none;
}

.slider-input {
  position: absolute;
  z-index: 3;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: grab;
  touch-action: none;
}

.slider-input:active {
  cursor: grabbing;
}

.slider-handle {
  position: absolute;
  z-index: 2;
  top: 2px;
  display: flex;
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  border: 1px solid #e5e7eb;
  border-radius: 5px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  color: #3b82f6;
  font-size: 27px;
  line-height: 1;
  pointer-events: none;
  transition: left 0.12s ease-out, box-shadow 0.12s ease-out;
}

.slider-handle span {
  transform: translateY(-1px);
}

.slider-track.is-dragging .slider-handle {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.14);
}

.slider-track.is-dragging .slider-handle,
.slider-track.is-dragging .slider-progress {
  transition: none;
}

.copy-link-button {
  display: flex;
  width: 100%;
  height: 46px;
  align-items: center;
  justify-content: center;
  gap: 7px;
  margin-top: 16px;
  border: 1px solid #d9e6fb;
  border-radius: 8px;
  background: #f5f9ff;
  color: #3578d4;
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.copy-link-button:active {
  border-color: #bdd5f7;
  background: #eaf3ff;
  transform: scale(0.99);
}

.copy-link-button:focus-visible {
  outline: 2px solid #246ee8;
  outline-offset: 2px;
}

.copy-link-icon {
  display: inline-flex;
  width: 17px;
  height: 17px;
  align-items: center;
  justify-content: center;
  border: 1px solid currentColor;
  border-radius: 4px;
  font-size: 14px;
  line-height: 1;
  transform: rotate(45deg);
}

.slider-input:focus-visible + .slider-handle {
  outline: 2px solid #246ee8;
  outline-offset: 2px;
}

@media (max-width: 359px) {
  .wechat-slider-gate {
    padding: 100px 14px 20px;
  }

  .slider-card {
    width: 100%;
  }

  .slider-card h1 {
    font-size: 19px;
  }
}
</style>
